import React, { useEffect, useState } from "react";
import { api, apiJson } from "@/api/client.js";
import { PWSWorkModal } from "@/components/PWSWorkModal.jsx";
import { planLine } from "@/components/DayTeams.jsx";

const ORDER = ["mem_luc", "mem_eric", "mem_marcel", "mem_pierre", "mem_steve", "mem_dan"];

function mark(value) {
  return value ? "✓" : "—";
}

function when(stamp) {
  if (!stamp) return "À confirmer";
  const date = new Date(stamp);
  if (Number.isNaN(date.getTime())) return "À confirmer";
  return new Intl.DateTimeFormat("fr-CA", {
    timeZone: "America/Toronto", day: "numeric", month: "short", hour: "numeric", minute: "2-digit",
  }).format(date);
}

function alertTone(id) {
  if (id.startsWith("sepaq") || id.startsWith("map") || id === "dups") return "urgent";
  if (id.startsWith("wait") || id === "check") return "todo";
  return "info";
}

function collectLabel(status) {
  if (status === "received") return "reçu";
  if (status === "none") return "aucun";
  return "en attente";
}

export function AdminCockpit({ onOpen, planDay }) {
  const [phones, setPhones] = useState([]);
  const [group, setGroup] = useState(null);
  const [teams, setTeams] = useState([]);
  const [fiche, setFiche] = useState(null);
  const [resetFor, setResetFor] = useState(null);

  function reload() {
    api("/api/nordquest").then((body) => setPhones(body.hunters || [])).catch(() => setPhones([]));
    api("/api/nordquest/group").then((body) => { if (body?.hunters) setGroup(body); }).catch(() => setGroup(null));
    if (planDay) api(`/api/day-plan?day=${planDay}`).then((body) => setTeams(body?.teams || [])).catch(() => setTeams([]));
  }

  useEffect(() => { reload(); }, [planDay]);

  const byPhone = Object.fromEntries(phones.map((row) => [row.member_id, row]));
  const byDrop = Object.fromEntries((group?.hunters || []).map((row) => [row.member_id, row]));
  const latest = group?.latest;
  const preview = group?.preview || {};
  const alerts = [];
  ORDER.forEach((id) => {
    const phone = byPhone[id];
    const drop = byDrop[id];
    const name = phone?.first_name || drop?.first_name || id;
    if (phone && !phone.sepaq_downloaded) alerts.push({ id: `sepaq-${id}`, text: `${name} n’a pas installé la carte Sépaq`, open: "nordquest" });
    if (latest && latest.code !== "1" && drop && drop.installed_mark !== "ok") alerts.push({ id: `map-${id}`, text: `${name} n’a pas confirmé V${latest.code}`, open: "group-map" });
    if (drop?.status === "waiting") alerts.push({ id: `wait-${id}`, text: `${name} n’a pas répondu à la collecte`, open: "nordquest-drop" });
  });
  if ((preview.unresolved || 0) > 0) alerts.push({ id: "check", text: `${preview.unresolved} nouveau${preview.unresolved > 1 ? "x" : ""} point${preview.unresolved > 1 ? "s" : ""} à vérifier`, open: "group-map" });
  if ((preview.duplicates || []).length) alerts.push({ id: "dups", text: `${preview.duplicates.length} doublon${preview.duplicates.length > 1 ? "s" : ""}`, open: "group-map" });
  if (!teams.length) alerts.push({ id: "plan", text: "Plan du jour : aucune équipe enregistrée", open: "organize" });

  const fichePhone = byPhone[fiche] || {};
  const ficheDrop = byDrop[fiche] || {};

  async function markMap(memberId, action, code) {
    await apiJson("/api/nordquest/group/map-state", { member_id: memberId, action, code: code || "" }, "POST");
    reload();
  }

  async function resetPhone() {
    if (!resetFor) return;
    await apiJson(`/api/nordquest/status/${resetFor}`, {
      installed: false, map_opened: false, sepaq_downloaded: false, points_imported: false, offline_ready: false, lessons: [],
    }, "PATCH");
    setResetFor(null);
    reload();
  }

  return (
    <div className="og-hunt38 is-admin-board" data-testid="admin-cockpit">
      <header className="og-admin-hero">
        <p>Mode Admin</p>
        <h2>Cockpit de l’expédition</h2>
      </header>
      <article className="og-hunt38__card" data-testid="admin-watch">
        <h2>À surveiller {alerts.length ? <span className="og-count-pill">{alerts.length}</span> : null}</h2>
        {alerts.length ? (
          <ul className="og-alert-list">
            {alerts.map((row) => (
              <li key={row.id}>
                <button type="button" className={`og-btn og-alert is-${alertTone(row.id)}`} data-testid={`alert-${row.id}`} onClick={() => onOpen(row.open)}>{row.text}</button>
              </li>
            ))}
          </ul>
        ) : <p>Rien à surveiller pour le moment.</p>}
      </article>

      <article className="og-hunt38__card" data-testid="admin-hunters">
        <h2>Les 6 chasseurs</h2>
        <table>
          <thead>
            <tr>
              <th>Chasseur</th><th>NordQuest</th><th>Sépaq</th><th>Carte groupe</th><th>Hors ligne</th><th>Synchro</th><th>État</th>
            </tr>
          </thead>
          <tbody>
            {ORDER.map((id) => {
              const phone = byPhone[id] || {};
              const drop = byDrop[id] || {};
              const name = phone.first_name || drop.first_name || id;
              return (
                <tr key={id} data-testid={`admin-row-${id}`}>
                  <td><button type="button" className="og-btn" data-testid={`admin-open-${id}`} onClick={() => setFiche(id)}>{name}</button></td>
                  <td>{mark(phone.installed)}</td>
                  <td>{mark(phone.sepaq_downloaded)}</td>
                  <td>{drop.installed ? `V${drop.installed}` : "V1"}</td>
                  <td>{mark(phone.offline_ready)}</td>
                  <td>{when(phone.updated_at)}</td>
                  <td>{phone.ready ? "prêt" : "pas prêt"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </article>

      <article className="og-hunt38__card og-admin-map" data-testid="admin-map">
        <h2>Carte du groupe</h2>
        <p className="og-admin-map__shot" aria-hidden="true" />
        <p>Carte actuelle : V{latest?.code || "1"} · {latest?.point_count || 9} points</p>
        <p>Nouveaux du jour : {preview.new_count || 0}</p>
        <p>À vérifier : {preview.unresolved || 0} · Doublons : {(preview.duplicates || []).length}</p>
        <p>Prochaine version : V{preview.next_code || "2"}</p>
        <div className="og-now__actions">
          <button type="button" className="og-btn" data-testid="admin-open-nordquest" onClick={() => onOpen("nordquest")}>NordQuest</button>
          <button type="button" className="og-btn og-btn--primary" data-testid="admin-open-map" onClick={() => onOpen("group-map")}>Carte du groupe</button>
          <button type="button" className="og-btn" data-testid="admin-open-collect" onClick={() => onOpen("nordquest-drop")}>Collecte du jour</button>
          <button type="button" className="og-btn" data-testid="admin-open-tomorrow" onClick={() => onOpen("group-map")}>Préparer la carte de demain</button>
        </div>
        <h3>Collecte du jour</h3>
        <ul data-testid="admin-collect">
          {ORDER.map((id) => {
            const drop = byDrop[id];
            if (!drop) return null;
            return <li key={id}>{drop.first_name} — {collectLabel(drop.status)}{drop.point_count ? ` · ${drop.point_count} point${drop.point_count > 1 ? "s" : ""}` : ""}</li>;
          })}
        </ul>
        <h3>Cartes du groupe</h3>
        <ul data-testid="admin-versions">
          {(group?.versions || [{ code: "1", point_count: 9, date: "2026-08-29", new_count: 0 }]).map((row) => (
            <li key={row.code}>V{row.code} · {row.point_count} points · {row.date || "date à confirmer"} · publiée{row.code !== "1" && row.new_count ? ` · +${row.new_count}` : ""}{row.published_by ? ` · ${row.published_by}` : ""}</li>
          ))}
          {group?.draft ? <li>V{group.draft.code} · {group.draft.point_count} points · brouillon</li> : null}
        </ul>
        <h3>État des cartes</h3>
        <div className="og-mapstates" data-testid="admin-installs">
          <table data-testid="map-states">
            <thead>
              <tr><th>Chasseur</th><th>Version</th><th>Points attendus</th><th>État</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {(group?.hunters || []).map((row) => (
                <tr key={row.member_id} data-testid={`map-state-${row.member_id}`}>
                  <td>{row.first_name}</td>
                  <td>{row.version_label || "—"}</td>
                  <td>{row.expected_label || "—"}</td>
                  <td>{row.map_status || "Carte à installer"}</td>
                  <td>
                    <button type="button" className="og-btn" data-testid={`mark-v1-${row.member_id}`} onClick={() => markMap(row.member_id, "mark", "1")}>Marquer V1</button>
                    {(group?.versions || []).filter((version) => version.code !== "1").map((version) => (
                      <button type="button" className="og-btn" key={version.code} data-testid={`mark-v${version.code}-${row.member_id}`} onClick={() => markMap(row.member_id, "mark", version.code)}>Marquer V{version.code}</button>
                    ))}
                    <button type="button" className="og-btn" data-testid={`mark-uncertain-${row.member_id}`} onClick={() => markMap(row.member_id, "uncertain")}>Marquer état incertain</button>
                    <button type="button" className="og-btn" data-testid={`mark-repair-${row.member_id}`} onClick={() => markMap(row.member_id, "repair")}>Recommander réparation</button>
                    <button type="button" className="og-btn" data-testid={`mark-reset-${row.member_id}`} onClick={() => markMap(row.member_id, "reset")}>Réinitialiser confirmation</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </article>

      <article className="og-hunt38__card" data-testid="admin-plan">
        <h2>Plan du jour</h2>
        <p>{planLine(teams)}</p>
        <button type="button" className="og-btn" data-testid="admin-open-day" onClick={() => onOpen("day")}>Voir ma journée</button>
        <button type="button" className="og-btn og-btn--primary" data-testid="admin-organize" onClick={() => onOpen("organize")}>Organiser la journée</button>
      </article>

      <article className="og-hunt38__card" data-testid="admin-secondary">
        <h2>Repas, matériel, tâches</h2>
        <div className="og-now__actions">
          <button type="button" className="og-btn" onClick={() => onOpen("meals")}>Repas</button>
          <button type="button" className="og-btn" onClick={() => onOpen("gear")}>Matériel</button>
          <button type="button" className="og-btn" data-testid="admin-open-tasks" onClick={() => onOpen("gang")}>Tâches</button>
          <button type="button" className="og-btn" onClick={() => onOpen("vehicles")}>Véhicules</button>
          <button type="button" className="og-btn" onClick={() => onOpen("chalet")}>Chambres</button>
          <button type="button" className="og-btn" data-testid="admin-open-weather" onClick={() => onOpen("weather")}>Conditions de chasse</button>
        </div>
      </article>

      <article className="og-hunt38__card" data-testid="admin-sync">
        <h2>Synchronisation du groupe</h2>
        <button type="button" className="og-btn og-btn--primary" onClick={reload}>Synchroniser maintenant</button>
        <ul>
          {ORDER.map((id) => {
            const phone = byPhone[id] || {};
            const drop = byDrop[id] || {};
            const name = phone.first_name || drop.first_name;
            if (!name) return null;
            return <li key={id}>{name} · {when(phone.updated_at)} · carte V{drop.installed || "1"} · collecte {collectLabel(drop.status)}</li>;
          })}
        </ul>
      </article>

      {fiche ? (
        <PWSWorkModal title={fichePhone.first_name || ficheDrop.first_name || "Chasseur"} onClose={() => { setFiche(null); setResetFor(null); }} testId="admin-fiche" closeTestId="admin-fiche-close">
          <div data-testid="admin-fiche-body">
            <h3>Préparation</h3>
            <p>NordQuest : {fichePhone.ready ? "prêt" : "pas prêt"}</p>
            <button type="button" className="og-btn" data-testid="admin-fiche-tasks" onClick={() => onOpen(fiche)}>Tâches</button>
            <h3>NordQuest</h3>
            <p>Installé : {mark(fichePhone.installed)}</p>
            <p>Carte Sépaq : {mark(fichePhone.sepaq_downloaded)}</p>
            <p>Version groupe : V{ficheDrop.installed || "1"}</p>
            <p>Hors ligne : {mark(fichePhone.offline_ready)}</p>
            <p>Dernière mise à jour : {when(fichePhone.updated_at)}</p>
            <p>Nouveaux points du jour : {ficheDrop.point_count || 0} · {collectLabel(ficheDrop.status)}</p>
            <details>
              <summary>⋯</summary>
              <button type="button" className="og-btn" data-testid="admin-reset" onClick={() => setResetFor(fiche)}>Réinitialiser NordQuest</button>
            </details>
            {resetFor ? (
              <div data-testid="admin-reset-confirm">
                <p>Réinitialiser les étapes NordQuest de {fichePhone.first_name || "ce chasseur"} ?</p>
                <button type="button" className="og-btn" onClick={() => setResetFor(null)}>Annuler</button>
                <button type="button" className="og-btn og-btn--primary" data-testid="admin-reset-go" onClick={resetPhone}>Réinitialiser</button>
              </div>
            ) : null}
          </div>
        </PWSWorkModal>
      ) : null}
    </div>
  );
}
